import React from 'react';

import { Toast, Button } from '@miotech/mprod';

import { W } from '../../i18n';
import { Analytics, redirectToApplication } from '../..';

import PageWithFooter from '../PageWithFooter/PageWithFooter';

interface Props {
  errorCode: string | number;
  title: React.ReactNode;
  message: React.ReactNode;
  link: React.ReactNode;
}

function ErrorPage({ errorCode, title, message, link }: Props) {
  const getErrorPageView = (
    title: React.ReactNode,
    message: React.ReactNode,
    link: React.ReactNode,
  ) => (
    <PageWithFooter>
      <div className="mio-page-with-footer__normal-content">
        <div
          className="mio-page-with-footer__normal-content__image"
          style={{
            backgroundImage:
              'url("https://static.miotech.com/images/system/error-page-1000x630.svg")',
          }}
        />

        {title ? (
          <div className="mio-page-with-footer__normal-content__text">
            <div className="mio-page-with-footer__normal-content__title">
              {title}
            </div>
          </div>
        ) : (
          ''
        )}
        {message ? (
          <div className="mio-page-with-footer__normal-content__text">
            <div className="mio-page-with-footer__normal-content__message">
              {message}
            </div>
          </div>
        ) : (
          ''
        )}
        {link ? (
          <div className="mio-page-with-footer__normal-content__text">
            <div className="mio-page-with-footer__normal-content__link">
              {link}
            </div>
          </div>
        ) : (
          ''
        )}
      </div>
    </PageWithFooter>
  );

  const getCodeErrorPageView = (
    backgroundImage: string,
    title: React.ReactNode,
    message: React.ReactNode,
    link: React.ReactNode,
    buttonTitle: string,
    buttonFunc: () => void,
  ) => (
    <PageWithFooter>
      <div className="mio-page-with-footer__special-content">
        <div
          className="mio-page-with-footer__special-content__image"
          style={{
            backgroundImage:
              'url("https://static.miotech.com/images/system/planet-ring-465x109.svg")',
          }}
        >
          <div
            className="text"
            style={{
              backgroundImage: `url("${backgroundImage}")`,
            }}
          />
        </div>
        {title ? (
          <div className="mio-page-with-footer__special-content__title">
            {title}
          </div>
        ) : (
          ''
        )}
        {message ? (
          <div className="mio-page-with-footer__special-content__message">
            {message}
          </div>
        ) : (
          ''
        )}
        {link ? (
          <div className="mio-page-with-footer__special-content__link">
            {link}
          </div>
        ) : (
          ''
        )}
        {buttonTitle && buttonFunc ? (
          <Button type="neutral" onClick={buttonFunc}>
            {buttonTitle}
          </Button>
        ) : (
          ''
        )}
      </div>
    </PageWithFooter>
  );

  const errorMessageMapping: any = {
    ERROR: {
      title: (
        <div>
          {W('shared.pages.error.title1')}
          <br />
          {W('shared.pages.error.title2')}
        </div>
      ),
    },
    403: {
      title: W('shared.pages.403.title'),
      backgroundImage:
        'https://static.miotech.com/images/system/403-246x92.svg',
      buttonFunc: () => {
        Analytics.post('/api/user/logout')
          .then(response => {
            if (response.code === 0 || response.code === 27) {
              Toast.success('Log out success!');
              redirectToApplication('USER', '');
            } else {
              Toast.error('Log out failed!');
            }
          })
          .catch(() => {
            Toast.error('Log out failed!');
          });
      },
      buttonTitle: W('shared.errorPage.switchAccount'),
    },
    404: {
      title: W('shared.pages.404.title'),
      message: W('shared.pages.404.message'),
      link: <a href="/">{W('shared.pages.404.home')}</a>,
      backgroundImage:
        'https://static.miotech.com/images/system/404-250x95.svg',
    },
    500: {
      title: W('shared.pages.500.title'),
      message: W('shared.pages.500.message'),
      backgroundImage:
        'https://static.miotech.com/images/system/500-252x90.svg',
    },
  };

  let errorCodeString = String(errorCode);
  if (!(errorCodeString in errorMessageMapping)) {
    errorCodeString = 'ERROR';
  }

  let {
    title: finalTitle,
    message: finalMessage,
    link: finalLink,
    backgroundImage,
    buttonFunc,
    buttonTitle,
  } = errorMessageMapping[errorCodeString];
  if (title) {
    finalTitle = title;
  }
  if (message) {
    finalMessage = message;
  }
  if (link) {
    finalLink = link;
  }

  if (errorCode === 'ERROR') {
    return getErrorPageView(finalTitle, finalMessage, finalLink);
  }
  return getCodeErrorPageView(
    backgroundImage,
    finalTitle,
    finalMessage,
    finalLink,
    buttonTitle,
    buttonFunc,
  );
}

ErrorPage.defaultProps = {
  errorCode: 'ERROR',
  title: undefined,
  message: undefined,
  link: undefined,
} as Partial<Props>;

export default ErrorPage;
